import { ApiError } from '@commissionsight/sdk';
import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { Button, Card, Field, PausedBanner, inputClass } from '../components/ui.js';
import {
  type CarrierRequestRow,
  type DetectResponse,
  detectStatement,
  listCarrierRequests,
  requestCarrier,
} from '../lib/carrierSetup.js';
import { type TrainResult, trainCarrierMapping } from '../lib/trainAI.js';
import { useCarriers } from '../lib/useCarriers.js';

/**
 * Statement-FIRST carrier setup: drop a statement and we work out the rest —
 * recognize a known carrier (signature library / config scoring), teach the AI a
 * known carrier's new format, or file an "add my carrier" request when the
 * carrier isn't in the registry yet. A manual carrier picker remains as the
 * fallback path. All app-only (off the SDK).
 */
export function CarrierSetup() {
  const { token, baseUrl, aiPaused } = useAuth();
  // All carriers (not just configured ones) — the manual path sets up new formats.
  const { carriers } = useCarriers({ withConfig: false });

  const [file, setFile] = useState<File | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [detection, setDetection] = useState<DetectResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Teach-the-AI state (for a detected/chosen known carrier).
  const [trainBusy, setTrainBusy] = useState(false);
  const [trainResult, setTrainResult] = useState<TrainResult | null>(null);
  const [trainedCarrierName, setTrainedCarrierName] = useState('this carrier');

  // Unknown-carrier request state.
  const [reqName, setReqName] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [reqBusy, setReqBusy] = useState(false);
  const [reqDone, setReqDone] = useState<string | null>(null);
  const [reqError, setReqError] = useState<string | null>(null);

  // Manual fallback state.
  const [manualCarrierId, setManualCarrierId] = useState('');

  // The account's previous requests (pending + decided); reloaded after submitting.
  const [requests, setRequests] = useState<CarrierRequestRow[]>([]);
  const loadRequests = useCallback(() => {
    listCarrierRequests(baseUrl, token)
      .then(setRequests)
      .catch(() => {});
  }, [baseUrl, token]);
  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const resetOutcomes = () => {
    setDetection(null);
    setError(null);
    setTrainResult(null);
    setReqDone(null);
    setReqError(null);
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    resetOutcomes();
    if (!f) return;
    setDetecting(true);
    try {
      const d = await detectStatement(baseUrl, token, f);
      setDetection(d);
      if (d.matchKind === 'none') setReqName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file.');
    } finally {
      setDetecting(false);
    }
  };

  const train = async (carrierId: string, carrierName: string) => {
    if (!file || trainBusy) return;
    setTrainBusy(true);
    setError(null);
    try {
      setTrainedCarrierName(carrierName);
      setTrainResult(await trainCarrierMapping(baseUrl, token, carrierId, file));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Something went wrong.');
    } finally {
      setTrainBusy(false);
    }
  };

  const submitRequest = async () => {
    if (!file || reqBusy || reqName.trim().length < 2) return;
    setReqBusy(true);
    setReqError(null);
    try {
      const r = await requestCarrier(baseUrl, token, {
        file,
        name: reqName.trim(),
        note: reqNote.trim() || undefined,
      });
      setReqDone(r.requestedName);
      loadRequests();
    } catch (e) {
      setReqError(e instanceof ApiError ? e.message : 'Something went wrong.');
    } finally {
      setReqBusy(false);
    }
  };

  const detected = detection?.carrier ?? null;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Set up a carrier</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Drop in a sample statement — we detect the carrier and take it from there. No mapping to
          configure, ever.
        </p>
      </div>

      {/* How this works — the two situations people arrive here with. */}
      <div className="rounded-xl border border-emerald-200/70 bg-emerald-50/60 px-4 py-3 text-sm dark:border-emerald-900/50 dark:bg-emerald-950/20">
        <p className="font-medium text-emerald-900 dark:text-emerald-300">
          How carrier setup works
        </p>
        <ul className="mt-1.5 list-disc space-y-1 pl-5 text-emerald-900/80 dark:text-emerald-200/80">
          <li>
            <span className="font-medium">New carrier?</span> Upload one of its statements below. If
            we don't support the carrier yet, name it and we'll add it — you'll get an email when
            it's ready (usually within a day).
          </li>
          <li>
            <span className="font-medium">New statement format from a carrier we know?</span>{' '}
            Nothing to set up — uploads on the{' '}
            <Link to="/files" className="underline">
              Files page
            </Link>{' '}
            are recognized automatically, and a new format is learned on first upload. This screen
            just confirms it up front.
          </li>
        </ul>
      </div>

      {aiPaused && (
        <PausedBanner message="AI features are paused for your account. Add a payment method on the Profile page or contact support to resume. Detection still works; learning a new format needs AI." />
      )}

      <Card>
        <Field label="Sample statement (CSV or Excel) — we'll detect the carrier from this">
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={onFile}
            className="block w-full text-sm text-neutral-600 file:mr-3 file:rounded-lg file:border-0 file:bg-emerald-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-emerald-700 hover:file:bg-emerald-100 dark:text-neutral-300 dark:file:bg-emerald-950/40 dark:file:text-emerald-300"
          />
        </Field>
        <p className="mt-2 text-xs text-neutral-400">
          Only the column headers and masked samples ever reach the AI or our review queue — never
          member data.
        </p>
      </Card>

      {detecting && (
        <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900">
          Reading the statement…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Outcome: we recognize the carrier. */}
      {detection && detected && !trainResult && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm dark:border-emerald-900/60 dark:bg-emerald-950/30">
          <p className="font-semibold text-emerald-800 dark:text-emerald-300">
            This looks like {detected.name ?? 'a known carrier'}
            {detection.matchKind === 'signature' ? ' — we know this exact format.' : '.'}
          </p>
          {detected.hasActiveConfig ? (
            <>
              <p className="mt-1 text-emerald-700 dark:text-emerald-400">
                It's already set up for your account. Upload the statement and it'll be scored in
                seconds.
              </p>
              <Link
                to="/files"
                className="mt-3 inline-flex rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700"
              >
                Upload a statement →
              </Link>
            </>
          ) : (
            <>
              <p className="mt-1 text-emerald-700 dark:text-emerald-400">
                We know the carrier but haven't learned this format for your account yet. One click
                teaches it.
              </p>
              <Button
                className="mt-3"
                onClick={() => train(detected.id, detected.name ?? 'this carrier')}
                loading={trainBusy}
                disabled={aiPaused}
              >
                Set it up with AI
              </Button>
            </>
          )}
          {detection.ambiguous && detection.ranked.length > 1 && (
            <p className="mt-2 text-xs text-emerald-700/70 dark:text-emerald-400/70">
              Not it? Other close matches:{' '}
              {detection.ranked
                .filter((r) => r.carrierId !== detected.id)
                .map((r) => (
                  <button
                    type="button"
                    key={r.carrierId}
                    onClick={() => train(r.carrierId, r.name ?? 'this carrier')}
                    className="mr-2 underline hover:text-emerald-900 dark:hover:text-emerald-200"
                  >
                    {r.name ?? r.carrierId}
                  </button>
                ))}
              or request it as a new carrier below.
            </p>
          )}
        </div>
      )}

      {/* Outcome: unknown carrier → request it. */}
      {detection && !detected && !reqDone && (
        <Card>
          <p className="text-sm font-semibold">We don't recognize this carrier yet</p>
          <p className="mt-1 text-sm text-neutral-500">
            Tell us who it is and we'll add it — you'll get an email when it's ready to upload
            (usually within a day). We keep only the statement's column layout, never member data.
          </p>
          <div className="mt-4 space-y-3">
            <Field label="Carrier name">
              <input
                className={inputClass}
                value={reqName}
                onChange={(e) => setReqName(e.target.value)}
                placeholder="e.g. Ambetter of Texas"
                maxLength={120}
              />
            </Field>
            <Field label="Anything we should know? (optional)">
              <input
                className={inputClass}
                value={reqNote}
                onChange={(e) => setReqNote(e.target.value)}
                placeholder="e.g. ACA marketplace, statements come monthly as Excel"
                maxLength={500}
              />
            </Field>
            {reqError && <p className="text-sm text-rose-600">{reqError}</p>}
            <Button onClick={submitRequest} loading={reqBusy} disabled={reqName.trim().length < 2}>
              Request this carrier
            </Button>
          </div>
        </Card>
      )}

      {reqDone && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm dark:border-emerald-900/60 dark:bg-emerald-950/30">
          <p className="font-semibold text-emerald-800 dark:text-emerald-300">
            Request received — we're adding {reqDone}.
          </p>
          <p className="mt-1 text-emerald-700 dark:text-emerald-400">
            You'll get an email as soon as it's ready to upload. Track it under "Your carrier
            requests" below.
          </p>
        </div>
      )}

      {trainResult && <ResultCard result={trainResult} carrierName={trainedCarrierName} />}

      {/* Manual fallback — pick the carrier yourself and teach its format. */}
      <details className="group rounded-xl border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-800 dark:bg-neutral-900">
        <summary className="cursor-pointer select-none text-sm font-medium text-neutral-600 dark:text-neutral-300">
          Prefer to pick the carrier manually?
        </summary>
        <div className="mt-3 space-y-3">
          <Field label="Carrier">
            <select
              className={inputClass}
              value={manualCarrierId}
              onChange={(e) => setManualCarrierId(e.target.value)}
              disabled={aiPaused}
            >
              <option value="">Select a carrier…</option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Button
            onClick={() => {
              const chosen = carriers.find((c) => c.id === manualCarrierId);
              if (chosen) void train(chosen.id, chosen.name);
            }}
            loading={trainBusy}
            disabled={!manualCarrierId || !file || aiPaused}
          >
            Set it up with AI
          </Button>
          {!file && (
            <p className="text-xs text-neutral-400">Choose a sample statement above first.</p>
          )}
        </div>
      </details>

      {/* Previous requests. */}
      {requests.length > 0 && (
        <Card className="p-0">
          <div className="border-b border-neutral-200 px-5 py-3 text-sm font-medium dark:border-neutral-800">
            Your carrier requests
          </div>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.requestedName}</p>
                  {r.status === 'merged' && r.resolvedCarrierName && (
                    <p className="text-xs text-neutral-400">Supported as {r.resolvedCarrierName}</p>
                  )}
                </div>
                <RequestStatusChip status={r.status} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function RequestStatusChip({ status }: { status: CarrierRequestRow['status'] }) {
  const styles: Record<CarrierRequestRow['status'], { label: string; cls: string }> = {
    pending: {
      label: 'pending review',
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    },
    approved: {
      label: 'added',
      cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    merged: {
      label: 'already supported',
      cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    },
    rejected: {
      label: 'declined',
      cls: 'bg-neutral-200 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
    },
  };
  const s = styles[status];
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

function ResultCard({ result, carrierName }: { result: TrainResult; carrierName: string }) {
  const reused = result.action === 'reuse' || result.action === 'reuse_candidate';
  const headline = reused
    ? `We already recognize ${carrierName} statements.`
    : `Done — we learned ${carrierName}'s statement format.`;
  const detail = reused
    ? 'You can upload your statements now and they’ll be mapped automatically.'
    : result.coverage
      ? `${result.coverage.mapped} of ${result.coverage.total} sample rows recognized (we found the member and commission columns). Future uploads of this carrier are mapped automatically.`
      : 'Future uploads of this carrier are now recognized automatically.';

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm dark:border-emerald-900/60 dark:bg-emerald-950/30">
      <p className="font-semibold text-emerald-800 dark:text-emerald-300">{headline}</p>
      <p className="mt-1 text-emerald-700 dark:text-emerald-400">{detail}</p>
      {result.usingByok && (
        <p className="mt-1 text-xs text-emerald-600/80 dark:text-emerald-500/80">
          Ran on your own AI key.
        </p>
      )}
      <Link
        to="/files"
        className="mt-3 inline-flex rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-emerald-700"
      >
        Upload a statement →
      </Link>
    </div>
  );
}
